/**
 * @description
 * Report service for managing VSCode Lens report schedules and generated reports.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type {
  ReportSchedule,
  GeneratedReport,
  VSCodeLensTable,
} from "../../../types/vscode-lens";

/**
 * Report service for VSCode Lens reports
 */
export class ReportService extends BaseAPIClient {
  /**
   * Get all report schedules
   */
  async getReportSchedules(filters?: {
    enabled?: boolean;
    table?: VSCodeLensTable;
  }): Promise<ReportSchedule[]> {
    const params = new URLSearchParams();

    if (filters?.enabled !== undefined)
      params.set("enabled", filters.enabled.toString());
    if (filters?.table) params.set("table", filters.table);

    const data = await this.request<{ schedules: ReportSchedule[] }>(
      `/vscode-lens/reports/schedules?${params.toString()}`,
    );
    return data.schedules;
  }

  /**
   * Get a specific report schedule
   */
  async getReportSchedule(id: string): Promise<ReportSchedule> {
    const data = await this.request<{ schedule: ReportSchedule }>(
      `/vscode-lens/reports/schedules/${id}`,
    );
    return data.schedule;
  }

  /**
   * Create a new report schedule
   */
  async createReportSchedule(
    schedule: Omit<
      ReportSchedule,
      "id" | "createdAt" | "updatedAt" | "lastRun" | "nextRun"
    >,
  ): Promise<ReportSchedule> {
    const data = await this.request<{ schedule: ReportSchedule }>(
      "/vscode-lens/reports/schedules",
      {
        method: "POST",
        body: JSON.stringify(schedule),
      },
    );
    return data.schedule;
  }

  /**
   * Update a report schedule
   */
  async updateReportSchedule(
    id: string,
    updates: Partial<ReportSchedule>,
  ): Promise<ReportSchedule> {
    const data = await this.request<{ schedule: ReportSchedule }>(
      `/vscode-lens/reports/schedules/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      },
    );
    return data.schedule;
  }

  /**
   * Delete a report schedule
   */
  async deleteReportSchedule(id: string): Promise<void> {
    await this.request(`/vscode-lens/reports/schedules/${id}`, {
      method: "DELETE",
    });
  }

  /**
   * Run a report schedule manually
   */
  async runReportSchedule(
    id: string,
  ): Promise<{ message: string; scheduleId: string; status: string }> {
    return this.request<{
      message: string;
      scheduleId: string;
      scheduleName: string;
      status: string;
    }>(`/vscode-lens/reports/schedules/${id}/run`, { method: "POST" });
  }

  /**
   * Get generated reports history
   */
  async getGeneratedReports(options?: {
    scheduleId?: string;
    table?: VSCodeLensTable;
    status?: "generating" | "completed" | "failed";
    limit?: number;
    offset?: number;
  }): Promise<{
    reports: GeneratedReport[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();

    if (options?.scheduleId) params.set("scheduleId", options.scheduleId);
    if (options?.table) params.set("table", options.table);
    if (options?.status) params.set("status", options.status);
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());

    return this.request<{
      reports: GeneratedReport[];
      total: number;
      limit: number;
      offset: number;
    }>(`/vscode-lens/reports/history?${params.toString()}`);
  }

  /**
   * Get a specific generated report
   */
  async getGeneratedReport(id: string): Promise<GeneratedReport> {
    const data = await this.request<{ report: GeneratedReport }>(
      `/vscode-lens/reports/${id}`,
    );
    return data.report;
  }

  /**
   * Get download URL for a report
   */
  async getReportDownloadUrl(
    id: string,
    format: "pdf" | "csv",
  ): Promise<{
    downloadUrl: string;
    filename: string;
    size: number;
    expiresAt: number;
  }> {
    return this.request<{
      downloadUrl: string;
      filename: string;
      size: number;
      expiresAt: number;
    }>(`/vscode-lens/reports/${id}/download/${format}`);
  }

  /**
   * Delete a generated report
   */
  async deleteGeneratedReport(id: string): Promise<void> {
    await this.request(`/vscode-lens/reports/${id}`, {
      method: "DELETE",
    });
  }

  /**
   * Cleanup expired reports
   */
  async cleanupExpiredReports(): Promise<{ deletedCount: number }> {
    return this.request<{ message: string; deletedCount: number }>(
      "/vscode-lens/reports/cleanup/expired",
      { method: "DELETE" },
    );
  }
}

// Export singleton instance
export const reportService = new ReportService();
